// src/store/slices/uiSlice.js
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    // Loading states
    loading: {
        global: false,
        users: false,
        dashboard: false,
        auth: false
    },

    // Error states
    errors: {
        global: null,
        users: null,
        dashboard: null,
        auth: null
    },

    // Notification system
    notifications: [],
    nextNotificationId: 1,

    // Modal states
    modals: {
        userModal: {
            isOpen: false,
            mode: 'create', // 'create' or 'edit'
            userId: null
        },
        confirmModal: {
            isOpen: false,
            title: '',
            message: '',
            onConfirm: null,
            onCancel: null
        }
    },

    // Sidebar states
    sidebar: {
        isCollapsed: false,
        isMobileOpen: false,
        width: 256
    },

    // Breadcrumb
    breadcrumbs: [],

    // Page title
    pageTitle: '',

    // Search
    globalSearch: {
        query: '',
        isActive: false
    },

    // Table states
    tables: {
        users: {
            sortBy: 'name',
            sortOrder: 'asc',
            selectedRows: []
        }
    }
};

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        // Loading actions
        setLoading: (state, action) => {
            const { key, value } = action.payload;
            state.loading[key] = value;
        },
        setGlobalLoading: (state, action) => {
            state.loading.global = action.payload;
        },

        // Error actions
        setError: (state, action) => {
            const { key, error } = action.payload;
            state.errors[key] = error;
        },
        clearError: (state, action) => {
            const key = action.payload;
            state.errors[key] = null;
        },
        clearAllErrors: (state) => {
            Object.keys(state.errors).forEach((key) => {
                state.errors[key] = null;
            });
        },

        // Notification actions
        addNotification: (state, action) => {
            const notification = {
                id: state.nextNotificationId++,
                timestamp: Date.now(),
                ...action.payload
            };
            state.notifications.unshift(notification);

            // Auto-remove after 5 seconds if autoRemove is true
            if (notification.autoRemove !== false) {
                setTimeout(() => {
                    // This would need to be handled in a component or middleware
                    // For now, we'll just add the notification
                }, 5000);
            }
        },
        removeNotification: (state, action) => {
            const id = action.payload;
            state.notifications = state.notifications.filter((n) => n.id !== id);
        },
        clearAllNotifications: (state) => {
            state.notifications = [];
        },

        // Modal actions
        openModal: (state, action) => {
            const { modalName, data = {} } = action.payload;
            if (state.modals[modalName]) {
                state.modals[modalName].isOpen = true;
                Object.assign(state.modals[modalName], data);
            }
        },
        closeModal: (state, action) => {
            const modalName = action.payload;
            if (state.modals[modalName]) {
                state.modals[modalName].isOpen = false;
                // Reset to default state
                if (modalName === 'userModal') {
                    state.modals[modalName] = {
                        isOpen: false,
                        mode: 'create',
                        userId: null
                    };
                } else if (modalName === 'confirmModal') {
                    state.modals[modalName] = {
                        isOpen: false,
                        title: '',
                        message: '',
                        onConfirm: null,
                        onCancel: null
                    };
                }
            }
        },
        closeAllModals: (state) => {
            Object.keys(state.modals).forEach((modalName) => {
                state.modals[modalName].isOpen = false;
                if (modalName === 'userModal') {
                    state.modals[modalName] = {
                        isOpen: false,
                        mode: 'create',
                        userId: null
                    };
                } else if (modalName === 'confirmModal') {
                    state.modals[modalName] = {
                        isOpen: false,
                        title: '',
                        message: '',
                        onConfirm: null,
                        onCancel: null
                    };
                }
            });
        },

        // Sidebar actions
        toggleSidebar: (state) => {
            state.sidebar.isCollapsed = !state.sidebar.isCollapsed;
        },
        setSidebarCollapsed: (state, action) => {
            state.sidebar.isCollapsed = action.payload;
        },
        toggleMobileSidebar: (state) => {
            state.sidebar.isMobileOpen = !state.sidebar.isMobileOpen;
        },
        setMobileSidebarOpen: (state, action) => {
            state.sidebar.isMobileOpen = action.payload;
        },
        setSidebarWidth: (state, action) => {
            state.sidebar.width = action.payload;
        },

        // Breadcrumb actions
        setBreadcrumbs: (state, action) => {
            state.breadcrumbs = action.payload;
        },
        addBreadcrumb: (state, action) => {
            state.breadcrumbs.push(action.payload);
        },

        // Page title actions
        setPageTitle: (state, action) => {
            state.pageTitle = action.payload;
        },

        // Global search actions
        setGlobalSearch: (state, action) => {
            state.globalSearch.query = action.payload;
        },
        setGlobalSearchActive: (state, action) => {
            state.globalSearch.isActive = action.payload;
        },
        clearGlobalSearch: (state) => {
            state.globalSearch.query = '';
            state.globalSearch.isActive = false;
        },

        // Table actions
        setTableSort: (state, action) => {
            const { tableName, sortBy, sortOrder } = action.payload;
            if (state.tables[tableName]) {
                state.tables[tableName].sortBy = sortBy;
                state.tables[tableName].sortOrder = sortOrder;
            }
        },
        toggleTableSort: (state, action) => {
            const { tableName, column } = action.payload;
            if (state.tables[tableName]) {
                const table = state.tables[tableName];
                if (table.sortBy === column) {
                    table.sortOrder = table.sortOrder === 'asc' ? 'desc' : 'asc';
                } else {
                    table.sortBy = column;
                    table.sortOrder = 'asc';
                }
            }
        },
        setTableSelectedRows: (state, action) => {
            const { tableName, selectedRows } = action.payload;
            if (state.tables[tableName]) {
                state.tables[tableName].selectedRows = selectedRows;
            }
        },
        toggleTableRowSelection: (state, action) => {
            const { tableName, rowId } = action.payload;
            if (state.tables[tableName]) {
                const selectedRows = state.tables[tableName].selectedRows;
                const index = selectedRows.indexOf(rowId);
                if (index > -1) {
                    selectedRows.splice(index, 1);
                } else {
                    selectedRows.push(rowId);
                }
            }
        },
        clearTableSelection: (state, action) => {
            const tableName = action.payload;
            if (state.tables[tableName]) {
                state.tables[tableName].selectedRows = [];
            }
        }
    }
});

export const {
    setLoading,
    setGlobalLoading,
    setError,
    clearError,
    clearAllErrors,
    addNotification,
    removeNotification,
    clearAllNotifications,
    openModal,
    closeModal,
    closeAllModals,
    toggleSidebar,
    setSidebarCollapsed,
    toggleMobileSidebar,
    setMobileSidebarOpen,
    setSidebarWidth,
    setBreadcrumbs,
    addBreadcrumb,
    setPageTitle,
    setGlobalSearch,
    setGlobalSearchActive,
    clearGlobalSearch,
    setTableSort,
    toggleTableSort,
    setTableSelectedRows,
    toggleTableRowSelection,
    clearTableSelection
} = uiSlice.actions;

export default uiSlice.reducer;
